/**
 * Every wording that names a class or a group differently. Both keys of a pair must carry the same
 * placeholders and plural forms — enforced by translations.parity.spec.ts.
 */
export const CONTAINER_KEYS = {
  allInContainer: ['classes.studentsAllInClass', 'groups.studentsAllInGroup'],
  alreadyInContainerCount: ['classes.studentsAlreadyInClass', 'groups.studentsAlreadyInGroup'],
  amount: ['classes.classAmount', 'groups.groupAmount'],
  archive: ['classes.archive', 'groups.archive'],
  archiveBannerTitle: ['classes.archiveBannerTitle', 'groups.archiveBannerTitle'],
  archiveConfirmButton: ['classes.archiveClassConfirmButton', 'groups.archiveConfirmButton'],
  archiveConfirmDescription: ['classes.archiveClassConfirmDescription', 'groups.archiveConfirmDescription'],
  archiveInProgress: ['classes.archiveInProgress', 'groups.archiveInProgress'],
  archiveOverviewDescription: ['classes.archiveClassOverviewDescription', 'groups.archiveOverviewDescription'],
  bumpUp: ['classes.bumpUp', 'groups.bumpUp'],
  bumpUpArchiveHint: ['classes.bumpUpDialogDescription', 'groups.bumpUpDialogDescription'],
  bumpUpArchiveTitle: ['classes.bumpUpDialogTitle', 'groups.bumpUpDialogTitle'],
  bumpUpWithoutArchiving: ['classes.onlyBumpUp', 'groups.onlyBumpUp'],
  canManageAccessRightsTooltip: ['accessRights.canManageAccessRightsTooltip', 'groups.canManageAccessRightsTooltip'],
  canManageStudentsTooltip: ['accessRights.canManageStudentsTooltip', 'groups.canManageStudentsTooltip'],
  collectiveRatingWholeSelection: ['quickEntry.collectiveRatingWholeClass', 'groups.collectiveRatingWholeGroup'],
  container: ['general.class', 'groups.group'],
  data: ['classes.data', 'groups.data'],
  delete: ['classes.delete', 'groups.delete'],
  deleteBlockedByStudents: ['errorMessages.CLASS_HAS_STILL_STUDENTS', 'groups.deleteBlockedByStudents'],
  edit: ['classes.editClass', 'groups.editGroup'],
  finalizedOverviewDescription: ['classes.finalizedClassOverviewDescription', 'groups.finalizedOverviewDescription'],
  futureSchoolYearDescription: ['classes.futureSchoolYearConfirm.description', 'groups.futureSchoolYearDescription'],
  finalizedOverviewDescriptionAlt: [
    'classes.finalizedClassOverviewDescriptionAlt',
    'groups.finalizedOverviewDescriptionAlt',
  ],
  generalDataDescription: ['classes.generalDataDescription', 'groups.generalDataDescription'],
  mine: ['settings.class', 'groups.myGroup'],
  navTitle: ['navigation.class.class', 'groups.group'],
  onboardingAssigned: [
    'onboarding.finalize.titlePersonalSecondaryClass',
    'onboarding.finalize.titlePersonalSecondaryGroup',
  ],
  performanceVisualBlockedByArchived: [
    'performanceVisual.blockedByClassArchived',
    'groups.performanceVisualBlockedByArchived',
  ],
  proAccessRightsTooltip: ['accessRights.proAccessRightsTooltip', 'groups.proAccessRightsTooltip'],
  remove: ['classes.remove', 'groups.remove'],
  removeConfirmHint: ['classes.removeConfirmHint', 'groups.removeConfirmHint'],
  removeConfirmText: ['classes.removeConfirmText', 'groups.removeConfirmText'],
  reportNoStudents: ['reports.entriesRequired.noStudents.body', 'groups.reportNoStudents'],
  reportNoSubjects: ['reports.entriesRequired.noSubjects.body', 'groups.reportNoSubjects'],
  reportLevelAndContainer: ['reports.levelAndClass', 'groups.levelAndGroup'],
  schoolYearOverBannerDescription: [
    'classes.schoolYearOverBannerDescription',
    'groups.schoolYearOverBannerDescription',
  ],
  schoolYearOverBannerDescriptionNoPerm: [
    'classes.schoolYearOverBannerDescriptionNoPerm',
    'groups.schoolYearOverBannerDescriptionNoPerm',
  ],
  studentsDescription: ['classes.studentsDescription', 'groups.studentsDescription'],
  switchTo: ['general.switchToClass', 'groups.switchToGroup'],
  switchToAfterCreate: ['classes.goToClass', 'groups.switchToGroupAfterCreate'],
  team: ['general.classTeam', 'groups.team'],
  teamEmptyStateTitle: ['classTeam.emptyStateTitle', 'groups.teamEmptyStateTitle'],
  teamMemberRemoveDescription: ['accessRights.removeDescription', 'groups.teamMemberRemoveDescription'],
  teamMembersDescriptionAlt: ['classes.teamDescriptionAlt', 'groups.teamMembersDescriptionAlt'],
  teamMemberWillBeInvitedDescription: ['general.willBeInvitedDescription', 'groups.teamMemberWillBeInvitedDescription'],
  teamTip: ['classTeam.tip', 'groups.teamTip'],
  timelineSetupNoStudentSubjectsRequestMailBody: [
    'performanceAssessmentEntry.timelineSetup.noStudentSubjectsRequestMailBody',
    'groups.timelineSetupNoStudentSubjectsRequestMailBody',
  ],
  timelineSetupNoStudentSubjectsRequestMailSubject: [
    'performanceAssessmentEntry.timelineSetup.noStudentSubjectsRequestMailSubject',
    'groups.timelineSetupNoStudentSubjectsRequestMailSubject',
  ],
  timelineSetupNoStudentsMessage: [
    'performanceAssessmentEntry.timelineSetup.noStudentsMessage',
    'groups.timelineSetupNoStudentsMessage',
  ],
  timelineSetupNoStudentsRequestMailBody: [
    'performanceAssessmentEntry.timelineSetup.noStudentsRequestMailBody',
    'groups.timelineSetupNoStudentsRequestMailBody',
  ],
  timelineSetupNoStudentsRequestMailSubject: [
    'performanceAssessmentEntry.timelineSetup.noStudentsRequestMailSubject',
    'groups.timelineSetupNoStudentsRequestMailSubject',
  ],
  timelineSetupNoSubjectsMessage: [
    'performanceAssessmentEntry.timelineSetup.noSubjectsMessage',
    'groups.timelineSetupNoSubjectsMessage',
  ],
  tipAll: ['students.tipAll', 'groups.tipAll'],
  tipPerformanceAssessments: ['performanceAssessment.tipTeacher', 'groups.performanceAssessmentTipTeacher'],
  tipPerformanceVisuals: ['performanceVisual.tipTeacher', 'groups.performanceVisualTipTeacher'],
  tipSettings: ['students.tipSettings', 'groups.tipSettings'],
  tipStatistics: ['statistics.tip', 'groups.statisticsTip'],
  welcomeToNew: ['classes.welcomeToYourNewClass', 'groups.welcomeToYourNewGroup'],
  wholeSelection: ['students.all', 'groups.wholeGroup'],
  wholeSelectionWithName: ['students.wholeClassWithName', 'groups.wholeGroupWithName'],
  withoutContainer: ['classes.withoutClass', 'groups.withoutGroup'],
} as const satisfies Record<string, readonly [classKey: string, groupKey: string]>;

export type TContainerKey = keyof typeof CONTAINER_KEYS;

/** For callers that already know the type and cannot use a composable (plain functions, mode toggles). */
export const resolveContainerKey = (key: TContainerKey, isGroup: boolean): string =>
  CONTAINER_KEYS[key][isGroup ? 1 : 0];
